import { supabase } from './supabase';
import type {
  Ticket,
  TicketReply,
  TicketActivity,
  TicketStatus,
  TicketPriority,
  TicketCategory,
  TicketStats,
  CannedResponse,
  TicketUser,
  TicketAttachment,
} from '@/components/tickets/types';

// ─── Caches ──────────────────────────────────────────────────────────────────
const workspaceTicketsCache = new Map<string, Ticket[]>();
const ticketDetailsCache = new Map<string, Ticket>();
const ticketRepliesCache = new Map<string, TicketReply[]>();

// ─── Service ─────────────────────────────────────────────────────────────────
export const ticketService = {
  clearCache() {
    workspaceTicketsCache.clear();
    ticketDetailsCache.clear();
    ticketRepliesCache.clear();
  },

  // ─── TICKETS ────────────────────────────────────────────────────────

  async getTicketsByWorkspace(workspaceId: string): Promise<Ticket[]> {
    if (workspaceTicketsCache.has(workspaceId)) {
      return workspaceTicketsCache.get(workspaceId)!;
    }

    const { data, error } = await supabase
      .from('support_tickets')
      .select(`
        *,
        creator:profiles!support_tickets_created_by_fkey(id, first_name, last_name, email, avatar_url),
        assignee:profiles!support_tickets_assigned_to_fkey(id, first_name, last_name, email, avatar_url),
        client:profiles!support_tickets_client_id_fkey(id, first_name, last_name, email, avatar_url),
        reply_count:support_ticket_replies(count)
      `)
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    const tickets = (data || []).map(mapTicket);
    workspaceTicketsCache.set(workspaceId, tickets);
    return tickets;
  },

  async getTicketById(ticketId: string): Promise<Ticket | null> {
    if (ticketDetailsCache.has(ticketId)) {
      return ticketDetailsCache.get(ticketId)!;
    }

    const { data, error } = await supabase
      .from('support_tickets')
      .select(`
        *,
        creator:profiles!support_tickets_created_by_fkey(id, first_name, last_name, email, avatar_url),
        assignee:profiles!support_tickets_assigned_to_fkey(id, first_name, last_name, email, avatar_url),
        client:profiles!support_tickets_client_id_fkey(id, first_name, last_name, email, avatar_url),
        reply_count:support_ticket_replies(count)
      `)
      .eq('id', ticketId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    const ticket = mapTicket(data);
    ticketDetailsCache.set(ticketId, ticket);
    return ticket;
  },

  async createTicket(
    workspaceId: string,
    userId: string,
    data: {
      subject: string;
      description: string;
      priority?: TicketPriority;
      category?: TicketCategory;
      clientId?: string | null;
      assignedTo?: string | null;
      attachments?: TicketAttachment[];
      tags?: string[];
    }
  ): Promise<Ticket> {
    const { data: ticket, error } = await supabase
      .from('support_tickets')
      .insert({
        workspace_id: workspaceId,
        subject: data.subject,
        description: data.description,
        status: 'open' as TicketStatus,
        priority: data.priority || 'normal',
        category: data.category || 'general',
        created_by: userId,
        assigned_to: data.assignedTo || null,
        client_id: data.clientId || null,
        attachments: data.attachments || [],
        tags: data.tags || [],
      })
      .select(`
        *,
        creator:profiles!support_tickets_created_by_fkey(id, first_name, last_name, email, avatar_url),
        assignee:profiles!support_tickets_assigned_to_fkey(id, first_name, last_name, email, avatar_url),
        client:profiles!support_tickets_client_id_fkey(id, first_name, last_name, email, avatar_url)
      `)
      .single();

    if (error) throw error;

    workspaceTicketsCache.delete(workspaceId);

    // Log creation activity
    await supabase.from('support_ticket_activities').insert({
      ticket_id: ticket.id,
      user_id: userId,
      type: 'created',
      content: 'Ticket created',
      metadata: {},
    });

    return mapTicket(ticket);
  },

  async updateTicket(
    ticketId: string,
    updates: Partial<{
      subject: string;
      description: string;
      priority: TicketPriority;
      category: TicketCategory;
      tags: string[];
    }>
  ): Promise<Ticket> {
    const { data, error } = await supabase
      .from('support_tickets')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', ticketId)
      .select(`
        *,
        creator:profiles!support_tickets_created_by_fkey(id, first_name, last_name, email, avatar_url),
        assignee:profiles!support_tickets_assigned_to_fkey(id, first_name, last_name, email, avatar_url),
        client:profiles!support_tickets_client_id_fkey(id, first_name, last_name, email, avatar_url),
        reply_count:support_ticket_replies(count)
      `)
      .single();

    if (error) throw error;

    ticketDetailsCache.delete(ticketId);
    workspaceTicketsCache.clear();
    return mapTicket(data);
  },

  async changeStatus(
    ticketId: string,
    workspaceId: string,
    userId: string,
    status: TicketStatus,
    previousStatus: TicketStatus
  ): Promise<void> {
    const now = new Date().toISOString();
    const extra: Record<string, any> = { updated_at: now };
    if (status === 'resolved') extra.resolved_at = now;
    if (status === 'closed') extra.closed_at = now;

    const { error } = await supabase
      .from('support_tickets')
      .update({ status, ...extra })
      .eq('id', ticketId);

    if (error) throw error;

    await supabase.from('support_ticket_activities').insert({
      ticket_id: ticketId,
      user_id: userId,
      type: 'status_change',
      content: `Status changed from "${previousStatus}" to "${status}"`,
      metadata: { from: previousStatus, to: status },
    });

    ticketDetailsCache.delete(ticketId);
    workspaceTicketsCache.delete(workspaceId);
  },

  async assignTicket(
    ticketId: string,
    workspaceId: string,
    userId: string,
    assignedTo: string | null
  ): Promise<void> {
    const { error } = await supabase
      .from('support_tickets')
      .update({ assigned_to: assignedTo, updated_at: new Date().toISOString() })
      .eq('id', ticketId);

    if (error) throw error;

    await supabase.from('support_ticket_activities').insert({
      ticket_id: ticketId,
      user_id: userId,
      type: assignedTo ? 'assigned' : 'unassigned',
      content: assignedTo ? 'Ticket assigned' : 'Ticket unassigned',
      metadata: { assigned_to: assignedTo },
    });

    ticketDetailsCache.delete(ticketId);
    workspaceTicketsCache.delete(workspaceId);
  },

  async deleteTicket(ticketId: string, workspaceId: string): Promise<void> {
    const { error } = await supabase
      .from('support_tickets')
      .delete()
      .eq('id', ticketId);

    if (error) throw error;

    ticketDetailsCache.delete(ticketId);
    workspaceTicketsCache.delete(workspaceId);
  },

  // ─── REPLIES ────────────────────────────────────────────────────────

  async getReplies(ticketId: string): Promise<TicketReply[]> {
    if (ticketRepliesCache.has(ticketId)) {
      return ticketRepliesCache.get(ticketId)!;
    }

    const { data, error } = await supabase
      .from('support_ticket_replies')
      .select(`
        *,
        user:profiles(id, first_name, last_name, email, avatar_url)
      `)
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });

    if (error) throw error;

    const replies = (data || []).map(mapReply);
    ticketRepliesCache.set(ticketId, replies);
    return replies;
  },

  async addReply(
    ticketId: string,
    workspaceId: string,
    userId: string,
    content: string,
    isInternal: boolean,
    attachments: TicketAttachment[] = []
  ): Promise<TicketReply> {
    const { data: reply, error } = await supabase
      .from('support_ticket_replies')
      .insert({
        ticket_id: ticketId,
        user_id: userId,
        content,
        is_internal: isInternal,
        attachments,
      })
      .select(`
        *,
        user:profiles(id, first_name, last_name, email, avatar_url)
      `)
      .single();

    if (error) throw error;

    // Update ticket updated_at and mark first_responded_at if not yet set
    const now = new Date().toISOString();
    const updatePayload: Record<string, any> = { updated_at: now };

    if (!isInternal) {
      // Check if this is the first team reply (to set first_responded_at)
      const { data: ticket } = await supabase
        .from('support_tickets')
        .select('first_responded_at, created_by')
        .eq('id', ticketId)
        .single();

      if (ticket && !ticket.first_responded_at && ticket.created_by !== userId) {
        updatePayload.first_responded_at = now;
      }
    }

    await supabase
      .from('support_tickets')
      .update(updatePayload)
      .eq('id', ticketId);

    // Log activity
    await supabase.from('support_ticket_activities').insert({
      ticket_id: ticketId,
      user_id: userId,
      type: isInternal ? 'internal_note' : 'replied',
      content: isInternal ? 'Added internal note' : 'Replied to ticket',
      metadata: { is_internal: isInternal },
    });

    ticketRepliesCache.delete(ticketId);
    ticketDetailsCache.delete(ticketId);
    workspaceTicketsCache.delete(workspaceId);

    return mapReply(reply);
  },

  async updateReply(
    replyId: string,
    ticketId: string,
    content: string
  ): Promise<TicketReply> {
    const { data, error } = await supabase
      .from('support_ticket_replies')
      .update({ content, updated_at: new Date().toISOString() })
      .eq('id', replyId)
      .select(`
        *,
        user:profiles(id, first_name, last_name, email, avatar_url)
      `)
      .single();

    if (error) throw error;

    ticketRepliesCache.delete(ticketId);
    return mapReply(data);
  },

  async deleteReply(replyId: string, ticketId: string): Promise<void> {
    const { error } = await supabase
      .from('support_ticket_replies')
      .delete()
      .eq('id', replyId);

    if (error) throw error;
    ticketRepliesCache.delete(ticketId);
  },

  // ─── ACTIVITIES ─────────────────────────────────────────────────────

  async getActivities(ticketId: string): Promise<TicketActivity[]> {
    const { data, error } = await supabase
      .from('support_ticket_activities')
      .select(`
        *,
        user:profiles(id, first_name, last_name, email, avatar_url)
      `)
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []).map(mapActivity);
  },

  // ─── STATS ──────────────────────────────────────────────────────────

  async getTicketStats(workspaceId: string): Promise<TicketStats> {
    const { data, error } = await supabase
      .from('support_tickets')
      .select('status, priority, assigned_to, first_responded_at, created_at')
      .eq('workspace_id', workspaceId);

    if (error) throw error;

    const tickets = data || [];

    let totalResponseMs = 0;
    let respondedCount = 0;

    tickets.forEach(t => {
      if (t.first_responded_at) {
        const diff = new Date(t.first_responded_at).getTime() - new Date(t.created_at).getTime();
        totalResponseMs += diff;
        respondedCount++;
      }
    });

    const avg_first_response_hours = respondedCount > 0
      ? Math.round((totalResponseMs / respondedCount) / (1000 * 60 * 60) * 10) / 10
      : null;

    return {
      total: tickets.length,
      open: tickets.filter(t => t.status === 'open').length,
      in_progress: tickets.filter(t => t.status === 'in_progress').length,
      waiting_on_client: tickets.filter(t => t.status === 'waiting_on_client').length,
      resolved: tickets.filter(t => t.status === 'resolved').length,
      closed: tickets.filter(t => t.status === 'closed').length,
      urgent: tickets.filter(t => t.priority === 'urgent').length,
      unassigned: tickets.filter(t => !t.assigned_to).length,
      avg_first_response_hours,
    };
  },

  // ─── CANNED RESPONSES ────────────────────────────────────────────────

  async getCannedResponses(workspaceId: string): Promise<CannedResponse[]> {
    const { data, error } = await supabase
      .from('support_canned_responses')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('title', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async createCannedResponse(
    workspaceId: string,
    userId: string,
    data: { title: string; content: string; category?: string }
  ): Promise<CannedResponse> {
    const { data: row, error } = await supabase
      .from('support_canned_responses')
      .insert({ workspace_id: workspaceId, created_by: userId, ...data })
      .select()
      .single();

    if (error) throw error;
    return row;
  },

  async deleteCannedResponse(id: string): Promise<void> {
    const { error } = await supabase
      .from('support_canned_responses')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};

// ─── Mappers ─────────────────────────────────────────────────────────────────

function mapUser(row: any): TicketUser | undefined {
  if (!row) return undefined;
  return {
    id: row.id,
    name: `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.email,
    email: row.email,
    avatar: row.avatar_url,
  };
}

function mapTicket(row: any): Ticket {
  return {
    id: row.id,
    workspace_id: row.workspace_id,
    ticket_number: row.ticket_number,
    subject: row.subject,
    description: row.description || '',
    status: row.status || 'open',
    priority: row.priority || 'normal',
    category: row.category || 'general',
    created_by: row.created_by,
    assigned_to: row.assigned_to || null,
    client_id: row.client_id || null,
    attachments: row.attachments || [],
    tags: row.tags || [],
    satisfaction_rating: row.satisfaction_rating || null,
    satisfaction_comment: row.satisfaction_comment || null,
    first_responded_at: row.first_responded_at || null,
    resolved_at: row.resolved_at || null,
    closed_at: row.closed_at || null,
    created_at: row.created_at,
    updated_at: row.updated_at,
    creator: mapUser(row.creator),
    assignee: mapUser(row.assignee),
    client: mapUser(row.client),
    reply_count: Array.isArray(row.reply_count)
      ? row.reply_count[0]?.count ?? 0
      : (row.reply_count ?? 0),
  };
}

function mapReply(row: any): TicketReply {
  return {
    id: row.id,
    ticket_id: row.ticket_id,
    user_id: row.user_id,
    content: row.content,
    is_internal: row.is_internal ?? false,
    attachments: row.attachments || [],
    created_at: row.created_at,
    updated_at: row.updated_at,
    user: mapUser(row.user),
  };
}

function mapActivity(row: any): TicketActivity {
  return {
    id: row.id,
    ticket_id: row.ticket_id,
    user_id: row.user_id,
    type: row.type,
    content: row.content,
    metadata: row.metadata || {},
    created_at: row.created_at,
    user: mapUser(row.user),
  };
}
